// src/components/BottomNavigation/BottomNavigation.js - UPDATED with Messages list view handling
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Home,
  MessageCircle,
  Bell,
  User,
  Store
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../../firebase/config';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { signOut } from 'firebase/auth';

const BottomNavContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  box-sizing: border-box;
  /* Scales with the actual viewport (phone) width instead of a fixed px value —
     ~60px on a small ~320px phone up to ~74px on a large ~430px phone, capped
     either side so it never looks cramped or oversized. */
  height: clamp(60px, 17.5vw, 74px);
  background: ${props => `${props.theme?.colors?.background || '#000000'}F5`};
  backdrop-filter: blur(20px);
  border-top: 1px solid ${props => `${props.theme?.colors?.accent || '#800000'}30`};
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 0 clamp(0.4rem, 2.5vw, 0.9rem);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  z-index: 1000;

  /* Only show on mobile */
  @media (min-width: 769px) {
    display: none;
  }
`;

const NavButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: ${props => props.active ?
    props.theme?.colors?.accent || '#800000' :
    `${props.theme?.colors?.text || '#FFFFFF'}80`
  };
  cursor: pointer;
  padding: clamp(0.3rem, 1.3vw, 0.5rem) clamp(0.2rem, 1vw, 0.35rem);
  border-radius: 12px;
  transition: all 0.3s ease;
  position: relative;
  min-width: clamp(50px, 15vw, 64px);
  flex: 1;
  max-width: clamp(70px, 20vw, 88px);

  &:active {
    transform: scale(0.95);
  }

  .icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: clamp(3px, 1vw, 5px);
    position: relative;
  }

  /* Overrides the icon's numeric size prop so it scales with screen width too —
     CSS width/height win over the SVG's own presentational width/height attrs. */
  .icon-container svg {
    width: clamp(17px, 5vw, 21px);
    height: clamp(17px, 5vw, 21px);
  }

  .label {
    /* Lower floor so small phones (iPhone SE etc.) genuinely shrink instead of
       being held at an artificial minimum — "Notifications"/"Messages" were
       crowding/wrapping there before. Slightly higher ceiling for large phones. */
    font-size: clamp(7.5px, 2.4vw, 11.5px);
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    opacity: ${props => props.active ? 1 : 0.8};
  }

  .notification-badge-count {
    position: absolute;
    top: -4px;
    right: -8px;
    background: ${props => props.theme?.colors?.accent || '#800000'};
    color: white;
    border-radius: 10px;
    padding: clamp(1px, 0.5vw, 2px) clamp(4px, 1.5vw, 6px);
    font-size: clamp(9px, 2.3vw, 10.5px);
    font-weight: 700;
    min-width: clamp(16px, 4vw, 19px);
    text-align: center;
    border: 2px solid ${props => props.theme?.colors?.background || '#000000'};
    z-index: 10;
  }

  .notification-dot {
    position: absolute;
    top: -3px;
    right: -3px;
    width: clamp(7px, 2vw, 9.5px);
    height: clamp(7px, 2vw, 9.5px);
    border-radius: 50%;
    background: ${props => props.theme?.colors?.accent || '#800000'};
    display: ${props => props.hasNotification ? 'block' : 'none'};
    border: 2px solid ${props => props.theme?.colors?.background || '#000000'};
  }

  /* Active state indicator */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: ${props => props.active ? 'clamp(4px, 1.2vw, 5px)' : '0'};
    height: 3px;
    background: ${props => props.theme?.colors?.accent || '#800000'};
    border-radius: 2px 2px 0 0;
    transition: width 0.3s ease;
  }
`;

const BottomNavigation = ({ theme }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const [unreadMessages, setUnreadMessages] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  
  // ✅ NEW: Check if user is guest
  const [isGuestUser, setIsGuestUser] = useState(false);

  useEffect(() => {
  if (!auth.currentUser) return;
  
  const notifRef = collection(db, 'notifications');
  const q = query(
    notifRef,
    where('userId', '==', auth.currentUser.uid),
    where('read', '==', false)
  );
  
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const count = snapshot.size;
    setNotificationCount(count);
    console.log('🔔 Unread notifications:', count);
  });
  
  return () => unsubscribe();
}, []);
  
  useEffect(() => {
    const checkGuestStatus = () => {
      const user = auth.currentUser;
      const guestSession = localStorage.getItem('guest_session');
      
      if (user?.isAnonymous || guestSession) {
        setIsGuestUser(true);
      } else {
        setIsGuestUser(false);
      }
    };
    
    checkGuestStatus();
    const unsubscribe = auth.onAuthStateChanged(checkGuestStatus);
    return () => unsubscribe();
  }, []);

  
  const defaultTheme = {
    colors: {
      background: '#000000',
      text: '#FFFFFF',
      accent: '#800000'
    }
  };

  const navTheme = theme || defaultTheme;

  // Logout handler
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Check for unread messages
  useEffect(() => {
    if (!auth.currentUser) return;
    
    const userId = auth.currentUser.uid;
    const chatsRef = collection(db, 'chats');
    const q = query(
      chatsRef,
      where('participants', 'array-contains', userId)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let hasUnread = false;
      
      snapshot.docs.forEach(doc => {
        const chat = doc.data();
        if (chat.unreadCount && chat.unreadCount[userId] && chat.unreadCount[userId] > 0) {
          hasUnread = true;
        }
      });
      
      setUnreadMessages(hasUnread);
    });
    
    return () => unsubscribe();
  }, []);

  // Check for unread notifications
  useEffect(() => {
    if (!auth.currentUser) return;
    
    // TODO: Implement notification checking logic
    setUnreadNotifications(false);
  }, []);

  // ✅ UPDATED: Handle navigation with special logic for Messages button
const handleNavigation = (item) => {
  if (item.isAction && item.key === 'logout') {
    handleLogout();
  } else if (item.key === 'messages') {
    // ✅ FIX: If already on messages page, reload to reset to list view
    if (location.pathname === '/messages') {
      console.log('📱 Messages button clicked - reloading page to return to list view');
      window.location.reload();
    } else {
      // Not on messages page - navigate normally
      console.log('📱 Messages button clicked - navigating to messages');
      navigate(item.path);
    }
  } else {
    navigate(item.path);
  }
};

  const isPathActive = (path) => {
    if (!path) return false;
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const navigationItems = [
  {
    icon: <Home size={20} />,
    label: 'Home',
    path: '/',
    key: 'home',
    showAlways: true,
    showForGuest: true
  },
  {
    icon: <Store size={20} />,
    label: 'My Shop',
    path: user?.uid ? `/shop/${user.uid}` : '/shop/create/template',
    key: 'shop',
    showAlways: false,
    requiresAuth: true,
    showForGuest: false
  },
  {
    icon: <MessageCircle size={20} />,
    label: 'Messages',
    path: '/messages',
    key: 'messages',
    hasNotification: unreadMessages,
    showAlways: true,
    showForGuest: true
  },
  {
    icon: <Bell size={20} />,
    label: 'Notifications',
    path: '/notifications',
    key: 'notifications',
    hasNotification: notificationCount > 0,
    badgeCount: notificationCount,
    showAlways: true,
    showForGuest: false
  },
  {
    icon: <User size={19} />,
    label: isGuestUser ? 'Logout' : 'Profile',
    path: isGuestUser ? null : '/profile', // ✅ No path for guests
    key: isGuestUser ? 'logout' : 'profile', // ✅ Dynamic key
    showAlways: true,
    isAction: isGuestUser, // ✅ Action only for guests
    showForGuest: true
  }
];

  // ✅ Filter items based on authentication AND guest status
  const visibleItems = navigationItems.filter(item => {
    // For guests, only show items marked showForGuest: true
    if (isGuestUser) {
      return item.showForGuest === true;
    }
    
    // For authenticated users, existing logic
    if (item.showAlways) return true;
    if (item.requiresAuth) return isAuthenticated;
    return true;
  });

  return (
    <BottomNavContainer theme={navTheme}>
      {visibleItems.map((item) => (
        <NavButton
          key={item.key}
          theme={navTheme}
          active={isPathActive(item.path)}
          onClick={() => handleNavigation(item)}
          hasNotification={item.hasNotification}
        >
          <div className="icon-container">
            {item.icon}
            {item.badgeCount > 0 && (
              <div className="notification-badge-count">
                {item.badgeCount > 99 ? '99+' : item.badgeCount}
              </div>
            )}
            {item.hasNotification && !item.badgeCount && <div className="notification-dot"></div>}
          </div>
          <span className="label">{item.label}</span>
        </NavButton>
      ))}
      
    </BottomNavContainer>
  );
};

export default BottomNavigation;